<template>
  <LoadingOverlay :is-active="bookingApiStatus === 'LOADING'" />
  <GenericImageLayout
    v-if="pageContent && bookingApiStatus === 'SUCCESS'"
    :image-src="pageContent.heroImage.filename"
    :background-color="pageContent.backgroundColour"
    class="appointment-booked"
    mobile-image
  >
    <div class="appointment-details">
      <h1 class="appointment-details__heading h4">
        {{ pageContent.title }}
      </h1>
      <div
        v-for="booking in activeBookings"
        :key="booking.mopJobNumber"
        class="appointment-details__wrapper"
      >
        <Article
          v-if="booking.cancellationDate"
          :text-content="pageContent.text"
          :variable-set="{
            [JOB_CANCELLATION_DATE]: booking.cancellationDate,
          }"
          class="appointment-details__text"
          no-margin
        >
        </Article>
        <dl class="appointment-details__list">
          <dt>Booking reference</dt>
          <dd ref="booking-reference">{{ booking.displayJobNumber }}</dd>
          <dt>Address</dt>
          <dd ref="address">{{ booking.fullAddress }}</dd>
          <dt>Date</dt>
          <dd ref="appointment-date">{{ booking.appointmentDateFormatted }}</dd>
          <dt>Time</dt>
          <dd ref="appointment-time">{{ booking.appointmentTimeSlot }}</dd>
        </dl>
        <div class="appointment-details__button-container">
          <AppButton
            ref="reschedule-appointment-button"
            :to="{
              name: 'reschedule-appointment',
              query: { jobNumber: booking.mopJobNumber },
            }"
            variant="invertedPrimary"
          >
            Reschedule
          </AppButton>
        </div>
        <RouterLink
          v-if="booking.isEligibleForCancellation"
          :to="{
            name: 'cancel-booking',
            query: { jobNumber: booking.mopJobNumber },
          }"
          class="appointment-details__link"
        >
          Need to cancel your booking?
        </RouterLink>
      </div>
    </div>
  </GenericImageLayout>
</template>

<script>
import { mapGetters, mapState } from "vuex"
import Article from "@soenergy/frontend-library/src/components/Article.vue"
import GenericImageLayout from "@soenergy/frontend-library/src/components/GenericImageLayout"
import LoadingOverlay from "@soenergy/frontend-library/src/components/LoadingOverlay.vue"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import cmsPreviewMixin from "@soenergy/frontend-library/src/mixins/cmsPreviewMixin"
import appointmentDetails from "soenergy-cms-loader!?path=smbp/appointment-details"
import { JOB_CANCELLATION_DATE } from "@/variable_keys/smbp_variables.js"

export default {
  components: {
    GenericImageLayout,
    Article,
    LoadingOverlay,
    AppButton,
  },
  mixins: [cmsPreviewMixin({ story: appointmentDetails })],
  data() {
    return {
      JOB_CANCELLATION_DATE,
    }
  },
  computed: {
    ...mapState({
      bookingApiStatus: (state) => state.bookingDetails.bookingApiStatus,
    }),
    ...mapGetters("bookingDetails", ["activeBookings"]),
  },
  async mounted() {
    try {
      await this.$store.dispatch("bookingDetails/fetchBookingData")
      if (!this.$store.state.meterInstallEligibility) {
        await this.$store.dispatch("getMeterInstallEligibility")
      }
      if (!this.$store.getters["bookingDetails/hasBookings"]) {
        this.$router.push({ path: "/install/choose-appointment" })
      }
    } catch (e) {
      ErrorReporter.report(e)
    }
  },
}
</script>

<style lang="scss" scoped>
.appointment-details {
  padding: $space-8 $space-4;

  @include lg {
    padding: $space-13 $space-13 $space-13 $space-0;
  }

  @include xl {
    padding: $space-13 $space-16 $space-13 $space-0;
  }

  &__heading {
    text-transform: none;
    margin-bottom: $space-1;
  }

  &__wrapper:not(:first-of-type) {
    margin-top: $space-10;
  }

  &__text {
    margin-top: $space-7;
    font-size: $font-size-3;

    @include lg {
      font-size: $font-size-4;
      margin: $space-9 0 $space-6 0;
    }
  }

  &__list {
    font-size: $font-size-3;
    display: grid;
    grid-template-rows: auto;
    grid-row-gap: $space-3;
    margin-top: $space-8;

    dt {
      grid-column-start: 1;
      font-weight: $weight-medium;
    }

    dd {
      grid-column-start: 2;
    }

    @include lg {
      font-size: $font-size-4;
      grid-column-gap: $space-10;
      margin-top: $space-9;
    }
  }

  &__button-container {
    margin: $space-9 0 $space-3 0;

    &:not(:last-child) {
      margin-right: $space-10;
    }

    @include lg {
      margin-top: $space-10;
    }
  }

  &__link {
    font-size: $font-size-3;

    @include lg {
      font-size: $font-size-4;
    }
  }
}
</style>
